/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler} from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsAlt, dlsColor} from '../theme/theme';
import {Color, DLSTheme, IconProps} from '../types/types';

export interface CargoSenseIconProps extends IconProps {
  color?: Color
  zone1Fill?: number;
  zone2Fill?: number;
  zone3Fill?: number;
  zone4Fill?: number;
  zone5Fill?: number;
  onZoneClick?: ({zone, fill}: {zone: number; fill: number;}) => void;
  showLabels?: boolean;
  zoneOutlineColor?: string;
  zoneColor?: string;
  wrapperClass?: string;
}

interface ZoneRectProps {
  url: string;
  onClick?: MouseEventHandler<SVGRectElement>;
  style?: GenericObject
  transform: string;
  stroke: string;
  x?: string;
  y?: string;
}

// Static width values to keep consistency
export const iconWidth = '300';
const zoneWidth = '72.7558';

// event generator
const generateEvents = (onClick: Function) => {
  return (val: {zone: number; fill: number;}) => function(e) {
    onClick?.(val);
  };
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  iconWrapper : {
    width                          : `${iconWidth}px`,
    [theme.breakpoints.down('sm')] : {
      width : '100%'
    }
  },
  labelContainer : {
    display    : 'flex',
    alignItems : 'center'
  },
  zoneLabelContainer : {
    width   : `${zoneWidth}px`,
    padding : '6px'
  },
  zoneLabel : {
    ...dlsAlt('typography', 'cta2'),
    margin : '5px 0px'
  },
  zoneReading : {
    ...dlsAlt('typography', 'label2'),
    letterSpacing                  : '0.6px', // needed for triple digit % to fit
    color                          : dlsColor('grey3'),
    margin                         : '5px 0px',
    [theme.breakpoints.down('sm')] : {
      margin : '5px 2px'
    }
  }
}));

/**
 * Local component for simple re-use across the icon as zones
 */
const ZoneRect = ({url, onClick, style, stroke, transform, x = '-0.25', y = '0.25'}: ZoneRectProps) => (
  <rect fill={`url(#${url})`}
    style={style}
    onClick={onClick}
    x={x}
    y={y}
    width={zoneWidth}
    height='94.5'
    rx='1.75'
    transform={transform}
    stroke={stroke}
    strokeWidth='0.5' />
);

/*
 * Defines the Design Language System (DLS) CargoSenseIcon Icon component.
 */
export const CargoSenseIcon = (props: CargoSenseIconProps) => {
  const {
    zone1Fill,
    zone2Fill,
    zone3Fill,
    zone4Fill,
    zone5Fill,
    onZoneClick,
    showLabels = true,
    zoneOutlineColor = dlsColor('caBlue'),
    zoneColor = dlsColor('arcticBlue'),
    wrapperClass
  } = props;
  const classes   = useStyles();
  const zoneStyle = {cursor: 'pointer'};

  // if the number is not within a fill percentage range we wont display the fill
  const hasZone = (zone) => {
    return zone >= 0 && zone <= 100;
  };

  // lets us know how many zones have values so we can iterate over them.
  const zonesLabelsCount = () => {
    const zones = Object.keys(props).filter(prop => prop.includes('zone') && prop.includes('Fill'));
    return zones.length;
  };

  /* CLICK HANDLERS */
  const mouseZoneEvent = generateEvents(onZoneClick);
  const onZone1Click   = mouseZoneEvent({zone: 1, fill: zone1Fill});
  const onZone2Click   = mouseZoneEvent({zone: 2, fill: zone2Fill});
  const onZone3Click   = mouseZoneEvent({zone: 3, fill: zone3Fill});
  const onZone4Click   = mouseZoneEvent({zone: 4, fill: zone4Fill});
  const onZone5Click   = mouseZoneEvent({zone: 5, fill: zone5Fill});

  return (
    <div className={classnames(classes.iconWrapper, wrapperClass)}>
      {
        showLabels && (
          <div className={classes.labelContainer}>
            {
              Array.from(Array(zonesLabelsCount()), (e, num) => {
                const zoneNumber   = num + 1;
                const filled       = props[`zone${zoneNumber}Fill`];
                const percentAvail = 100 - filled;
                return (
                  <div className={classes.zoneLabelContainer}>
                    <p className={classes.zoneLabel}>
                      {translate('common:smarterTrailer.cargoSense.zoneLabel', {zoneNumber})}
                    </p>
                    <p className={classes.zoneReading}>
                      {
                        percentAvail >= 0 ? (
                          <>{translate('common:smarterTrailer.cargoSense.percentAvail', {percentAvail})}</>
                        ) : (
                          <>{translate('common:notAvailable')}</>
                        )
                      }
                    </p>
                  </div>
                );
              })
            }
          </div>
        )
      }
      <svg width={iconWidth} height='156' viewBox='0 0 421 156' fill='none'>
        <rect x='-0.25' y='0.25' width='9.26744' height='3.5' transform='matrix(-1 0 0 1 96.2209 150)' fill='white' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <path d='M85.9768 132L83.5349 128.5H4.90705V125' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <rect y='0.357688' width='3.45376' height='26.1879' transform='matrix(-0.698739 0.715376 0.698739 0.715376 396.242 120.102)' fill='white' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <path d='M411.489 116L420.023 121.263V126H1.00006V116H411.489Z' fill='white' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <path d='M359.715 137C359.715 147.361 367.917 155.75 378.023 155.75C388.129 155.75 396.331 147.361 396.331 137C396.331 126.639 388.129 118.25 378.023 118.25C367.917 118.25 359.715 126.639 359.715 137Z' fill='white' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <path d='M366.552 137C366.552 143.495 371.694 148.75 378.023 148.75C384.353 148.75 389.494 143.495 389.494 137C389.494 130.505 384.353 125.25 378.023 125.25C371.694 125.25 366.552 130.505 366.552 137Z' fill='#E0E0E0' stroke='#979797' strokeWidth='0.5'/>
        <path d='M313.808 137C313.808 147.361 322.011 155.75 332.116 155.75C342.222 155.75 350.424 147.361 350.424 137C350.424 126.639 342.222 118.25 332.116 118.25C322.011 118.25 313.808 126.639 313.808 137Z' fill='white' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <path d='M320.645 137C320.645 143.495 325.787 148.75 332.116 148.75C338.446 148.75 343.587 143.495 343.587 137C343.587 130.505 338.446 125.25 332.116 125.25C325.787 125.25 320.645 130.505 320.645 137Z' fill='#E0E0E0' stroke='#979797' strokeWidth='0.5'/>
        <rect x='-0.25' y='0.25' width='419.5' height='109.5' transform='matrix(-1 0 0 1 420.5 0)' fill='white' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <path d='M337 0V110' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <path d='M253 0V110' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <g clipPath='url(#clip1)'>
          <rect width='76' height='48' transform='matrix(-1 0 0 1 81.093 55)' fill={zoneColor}/>
        </g>
        {
          hasZone(zone1Fill) && (
            <ZoneRect url='zone1'
              style={zoneStyle}
              onClick={onZone1Click}
              transform='matrix(-1 0 0 1 80.593 8)'
              stroke={zoneOutlineColor} />
          )
        }
        <rect x='-0.25' y='0.25' width='73.7326' height='3.5' transform='matrix(-1 0 0 1 202.686 128)' fill='white' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <rect x='-0.25' y='0.25' width='73.7326' height='3.5' transform='matrix(-1 0 0 1 202.686 134)' fill='white' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <rect x='-0.25' y='-0.25' width='24.5' height='3.40698' transform='matrix(4.26948e-08 -1 -1 -4.47521e-08 198.779 140.5)' fill='white' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <rect x='-0.25' y='-0.25' width='24.5' height='3.40698' transform='matrix(4.26948e-08 -1 -1 -4.47521e-08 136.267 140.5)' fill='white' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <rect x='0.0615415' y='-0.352107' width='12.1121' height='3.46951' transform='matrix(0.81188 -0.583825 -0.565714 -0.824602 83.3639 135.03)' fill='white' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <rect x='-0.25' y='0.25' width='3.40698' height='26.5' transform='matrix(-1 0 0 1 414.64 127)' fill='white' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <rect x='-0.25' y='-0.25' width='29.5' height='3.40698' transform='matrix(4.26948e-08 -1 -1 -4.47521e-08 93.2905 151.5)' fill='white' stroke={dlsColor('grey3')} strokeWidth='0.5'/>
        <g clipPath='url(#clip2)'>
          <rect width='76' height='48' transform='matrix(-1 0 0 1 164.14 55)' fill={zoneColor}/>
        </g>
        {
          hasZone(zone2Fill) && (
            <ZoneRect url='zone2'
              style={zoneStyle}
              onClick={onZone2Click}
              transform='matrix(-1 0 0 1 162.64 8)'
              stroke={zoneOutlineColor} />
          )
        }
        <g clipPath='url(#clip3)'>
          <rect width='76' height='48' transform='matrix(-1 0 0 1 247.14 55)' fill={zoneColor}/>
        </g>
        {
          hasZone(zone3Fill) && (
            <ZoneRect url='zone3'
              style={zoneStyle}
              onClick={onZone3Click}
              transform='matrix(-1 0 0 1 246.64 8)'
              stroke={zoneOutlineColor} />
          )
        }
        <g clipPath='url(#clip4)'>
          <rect width='79' height='48' transform='matrix(-1 0 0 1 333.14 71)'/>
        </g>
        {
          hasZone(zone4Fill) && (
            <ZoneRect url='zone4'
              style={zoneStyle}
              onClick={onZone4Click}
              transform='matrix(-1 0 0 1 330.64 8)'
              stroke={zoneOutlineColor} />
          )
        }
        <g clipPath='url(#clip5)' />
        {
          hasZone(zone5Fill) && (
            <ZoneRect url='zone5'
              style={zoneStyle}
              onClick={onZone5Click}
              transform='matrix(-1 0 0 1 414.64 8)'
              stroke={zoneOutlineColor} />
          )
        }
        <defs>
          <pattern id='pattern0' patternContentUnits='objectBoundingBox' width='1' height='1'>
            <use xlinkHref='#image0' transform='translate(0 -0.00558279) scale(0.00108932 0.00179285)'/>
          </pattern>
          <clipPath id='clip1' />
          <clipPath id='clip2' />
          <clipPath id='clip3' />
          <clipPath id='clip4' />
          <clipPath id='clip5' />
          <>
            {
              Array.from(Array(zonesLabelsCount()), (e, num) => {
                const zone   = num + 1;
                const zoneId = `zone${zone}`;
                const fill   = `${props[`${zoneId}Fill`]}%`;
                return (
                  <linearGradient id={`zone${zone}`} x1='0%' y1='100%' x2='0%' y2='0%'>
                    <stop offset='0%' stopColor={zoneColor} />
                    <stop offset={fill} stopColor={zoneColor} />
                    <stop offset={fill} stopColor='transparent' />
                    <stop offset='100%' stopColor='transparent' />
                  </linearGradient>
                );
              })
            }
          </>
        </defs>
      </svg>
    </div>
  );
};
export default CargoSenseIcon;
